// src/pages/OdooConfigurations.tsx
import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout.tsx';
import { Toast } from '../components/Toast.tsx';
import { odooConfigAPI, OdooConfiguration } from '../services/api.ts';
import { PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { OdooConfigurationForm } from '../components/odoo/OdooConfigurationForm.tsx';
import { useSync } from '../contexts/SyncContext.tsx';
import { OdooConfigurationEditForm } from '../components/odoo/OdooConfigurationEditForm.tsx';

export const OdooConfigurations: React.FC = () => {
  const [configurations, setConfigurations] = useState<OdooConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editConfig, setEditConfig] = useState<OdooConfiguration | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    configId: number | null;
  }>({
    isOpen: false,
    configId: null,
  });

  const { isSyncing, startSync, updateSync, endSync } = useSync();

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await odooConfigAPI.getAll();
      if (response.success && response.data) {
        setConfigurations(response.data.configurations);
      }
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await odooConfigAPI.delete(id);
      setToast({
        message: 'Configuration deleted successfully',
        type: 'success',
      });
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
      setDeleteConfirmation({ isOpen: false, configId: null });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = await checkOddooConnection(values);
      if (!data) return;
      setLoading(true);
      await odooConfigAPI.create(values);
      setToast({
        message: 'Configuration created successfully',
        type: 'success',
      });
      setIsModalOpen(false);
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = (configId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get the stream URL - this will use Lambda Function URL if STREAM_API_URL is set
      let url = odooConfigAPI.getAuthorizationStream(configId);

      // Add token as query parameter
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}token=${token}`;

      // Begin syncing with an initial message
      startSync('Connecting to Odoo...');

      // Create the EventSource to listen to server-sent events
      const eventSource = new EventSource(url);

      // When a message is received, update the sync message
      eventSource.onmessage = (event) => {
        const data = event.data;

        // Check if this is the completion message
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'complete') {
            setToast({
              message: parsed.success
                ? 'Configuration synced successfully'
                : `Sync completed with ${parsed.errors} errors`,
              type: parsed.success ? 'success' : 'error',
            });
            eventSource.close();
            endSync();
            fetchConfigurations(); // Refresh the list
            return;
          }
        } catch {
          // Not JSON, just a status message
        }

        updateSync(data);
      };

      // Handle errors from the event stream
      eventSource.onerror = (error) => {
        console.error('Authorization error:', error);
        setToast({
          message: 'Failed to connect to sync stream',
          type: 'error',
        });
        eventSource.close();
        endSync();
      };
    } catch (error: any) {
      console.error('Authorization error:', error);
      setToast({ message: error.message, type: 'error' });
      endSync();
    }
  };

  const handleEdit = async (values: any) => {
    if (!editConfig) return;
    try {
      const data = await checkOddooConnection(values);
      if (!data) return;
      setLoading(true);
      await odooConfigAPI.update(editConfig?.id, values);
      setToast({
        message: 'Configuration created successfully',
        type: 'success',
      });
      setIsModalOpen(false);
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const checkOddooConnection = async (config: OdooConfiguration) => {
    const data = await odooConfigAPI.checkOdooConnectivity({
      url: config.url,
      db: config.db,
      username: config.username,
      password: config.password || '',
    });

    if (!data.success) {
      setToast({ message: data.message, type: 'error' });
      return false;
    }

    setToast({ message: data.message, type: 'success' });
    return true;
  };

  const filteredConfigurations = configurations.filter((config) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      config.name?.toLowerCase().includes(q) ||
      config.url?.toLowerCase().includes(q) ||
      config.db?.toLowerCase().includes(q) ||
      config.username?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center gap-4">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Odoo Configurations
            </h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-400">
              Manage your Odoo server configurations and connections.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3 sm:items-center w-full sm:w-auto sm:ml-8">
            <input
              type="search"
              className="w-full sm:w-72 p-2 border rounded shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="Search by name, URL, DB, user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:w-auto whitespace-nowrap"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Configuration
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-500">
                    Loading configurations...
                  </p>
                </div>
              ) : filteredConfigurations.length > 0 ? (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Database
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Username
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Created At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Updated At
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredConfigurations.map((config) => {
                        return (
                          <tr
                            key={config.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs">
                              <div className="break-words">{config.url}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {config.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div className="break-words">{config.db}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div className="break-words">
                                {config.username}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <div className="break-words">
                                {new Date(config.created_at).toLocaleString(
                                  'en-US',
                                  {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                  }
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <div className="break-words">
                                {new Date(config.updated_at).toLocaleString(
                                  'en-US',
                                  {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                  }
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                              <button
                                onClick={() => handleAuthorize(config.id)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                disabled={isSyncing}
                              >
                                {isSyncing ? (
                                  <>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.5}
                                      stroke="currentColor"
                                      className="size-6 animate-spin"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                      />
                                    </svg>
                                  </>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="size-6"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                    />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  checkOddooConnection(config);
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-6"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditConfig(config);
                                  setEditModalOpen(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-6"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                                  />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                    No configurations
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {searchTerm
                      ? 'Try a different search term.'
                      : 'Get started by adding a new Odoo configuration.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Odoo Configuration Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            ></div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <OdooConfigurationForm
                onSubmit={handleSubmit}
                onCancel={() => setIsModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {editConfig && editModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setEditModalOpen(false)}
            ></div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <OdooConfigurationEditForm
                onSubmit={handleEdit}
                onCancel={() => setEditModalOpen(false)}
                initialValues={editConfig}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() =>
                setDeleteConfirmation({ isOpen: false, configId: null })
              }
            ></div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Delete Configuration
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this Odoo configuration?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    if (deleteConfirmation.configId) {
                      handleDelete(deleteConfirmation.configId);
                    }
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() =>
                    setDeleteConfirmation({ isOpen: false, configId: null })
                  }
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};
